import React, { useEffect, useState } from "react";
import { useAuth } from "src/context/AuthContext";
import LoadingSpinner from "src/components/LoadingSpinner";
import { Modal } from "src/components/ui/Modal";
import { FormInput } from "src/components/ui/FormInput";
import { Button } from "src/components/ui/Button";
import FileUploadPhoto from "src/components/ui/FileUploadPhoto";
import { X } from "lucide-react";
import { useTutorExtendedProfileStore } from "src/store/tutorProfileStore";
import { supabase } from 'src/lib/supabaseClient';
import { TutorExperience } from "src/interfaces/TutorProfile";
import TutorProfileModal from "src/components/tutor/ProfileModal";

// Import tutor profile section components
import ProfileCardSection from "src/components/tutor/profile/ProfileCardSection";
import ProfileCompletenessSection from "src/components/tutor/profile/ProfileCompletenessSection";
import GeneralInformationSection from "src/components/tutor/profile/GeneralInformationSection";
import EducationSection from "src/components/tutor/profile/EducationSection";
import AchievementsSection from "src/components/tutor/profile/AchievementsSection";
import ExpertiseSection from "src/components/tutor/profile/ExpertiseSection";
import SubjectsSection from "src/components/tutor/profile/SubjectsSection";
import TeachingExperienceSection from "src/components/tutor/profile/TeachingExperienceSection";

const TutorProfile: React.FC = () => {
  const { user, profileData: authProfileData } = useAuth();

  // Use the tutor-specific store
  const {
    profileData: storeProfileData,
    pendingChanges,
    isLoading: isSaving,
    setIsEditing,
    editData,
    isEditing,
    setEditData,
    updateEditData,
    resetEditData,
    updateProfile,
    fetchProfile,
    photoUploadOpen,
    uploadType,
    selectedFile,
    uploadStatus,
    setPhotoUploadOpen,
    setSelectedFile,
    setUploadStatus,
    handleOpenPhotoUpload,
    activeSectionEdit,
    setActiveSectionEdit,
    // New methods for pending changes
    addPendingChanges,
    saveAllChanges,
    discardAllChanges,
    getDisplayData,
    isSectionModified,
    hasPendingChanges,
    modifiedSections,
    isLoading,
    error
  } = useTutorExtendedProfileStore();

  // Initialize profile data from AuthContext when component mounts or user changes
  useEffect(() => {
    if (user?.id && authProfileData) {
      console.log('Initializing Tutor Profile with AuthContext data:', authProfileData);
      fetchProfile(user.id, authProfileData);
    }
  }, [user?.id, authProfileData, fetchProfile]);

  // Get the display data (combination of profile data and pending changes)
  const profileData = getDisplayData();

  // Debug: Log the profile data being used by components
  console.log('Tutor Profile - profileData being used by components:', profileData);
  console.log('Tutor Profile - AuthContext profilePictureUrl:', authProfileData?.profilePictureUrl);
  console.log('Tutor Profile - Local store profilePictureUrl:', profileData.profilePictureUrl);

  // State for profile modal
  const [showProfileModal, setShowProfileModal] = useState(false);

  // Handler to view profile modal
  const handleViewProfile = () => {
    setShowProfileModal(true);
  };

  // Handler to close profile modal
  const handleCloseProfileModal = () => {
    setShowProfileModal(false);
  };

  // Handle file selection (for initial file selection, before cropping)
  const handleFileSelect = (file: File) => {
    // For CV uploads, directly set the selected file without cropping
    if (uploadType === 'cv') {
      setSelectedFile(file);
    } else {
      // For images, don't set selectedFile yet - this just opens the crop modal
    }
  };

  // Handle cropped file selection (this is the final processed file, ready for upload)
  const handleCroppedFileSelect = (file: File) => {
    // Set the cropped file - this makes it ready for upload when user clicks OK
    setSelectedFile(file);
    console.log('Cropped file ready for upload:', file.name);
  };

  // Handle file removal
  const handleFileRemove = () => {
    setSelectedFile(null);
  };

  // Handle photo upload (triggered by OK button after cropping)
  const handleUploadPhoto = async () => {
    if (!selectedFile) {
      console.log('No file selected for upload');
      return;
    }

    console.log('Starting upload process for:', selectedFile.name);

    try {
      setUploadStatus('uploading');

      // Dynamically import storage utils
      const {
      generateProfilePicturePath,
      generateCVFilePath,
      getBucketForUserType,
      generateUniqueFileName,
    } = await import('src/utils/storageUtils');

      // Generate a unique file name
      const fileName = generateUniqueFileName(selectedFile.name);

      // Create the file path based on upload type and user type
      let filePath: string;
      if (uploadType === 'profile') {
        filePath = generateProfilePicturePath('tutor', user?.id, fileName);
      } else if (uploadType === 'cv') {
        filePath = generateCVFilePath('tutor', user?.id, fileName);
      } else {
        filePath = `gallery/${user?.id}/${fileName}`;
      }

      // Get the appropriate bucket for the user type and upload type
      const bucket = getBucketForUserType('tutor', uploadType);

      // Upload file to Supabase Storage
      const { error: uploadError } = await supabase.storage
        .from(bucket)
        .upload(filePath, selectedFile);

      if (uploadError) {
        throw uploadError;
      }

      // Get the public URL
      const { data } = supabase.storage
        .from(bucket)
        .getPublicUrl(filePath);

      // Update profile with new file URL based on upload type
      if (uploadType === 'profile') {
        // Store the old profile picture URL before updating the profile
        const oldProfilePictureUrl = profileData.profilePictureUrl;

        // Update the profile with the new URL
        await updateProfile({
          ...profileData,
          profilePictureUrl: data.publicUrl
        });

        console.log('Profile picture updated successfully');

        // Clean up old profile picture in the background (don't await to avoid UI delays)
        if (oldProfilePictureUrl) {
          // Run cleanup in background without blocking UI
          import("src/utils/storageUtils").then(({ parseStorageUrl }) => {
            const parsedUrl = parseStorageUrl(oldProfilePictureUrl);
            if (parsedUrl) {
              supabase.storage
                .from(parsedUrl.bucket)
                .remove([parsedUrl.filePath])
                .then(({ error }) => {
                  if (error) {
                    console.warn('Error deleting old profile picture:', error);
                  } else {
                    console.log('Old profile picture deleted successfully');
                  }
                });
            }
          }).catch(error => {
            console.warn('Error processing old profile picture URL:', error);
          });
        }
      } else if (uploadType === 'cv') {
        // Store the old CV file path before updating the profile
        const oldCvFilePath = profileData.cv_file_path;

        // Update the profile with the new CV file path
        await updateProfile({
          ...profileData,
          cv_file_path: data.publicUrl
        });

        console.log('CV file updated successfully');

        // Clean up old CV file in the background (don't await to avoid UI delays)
        if (oldCvFilePath) {
          // Run cleanup in background without blocking UI
          import("src/utils/storageUtils").then(({ parseStorageUrl }) => {
            const parsedUrl = parseStorageUrl(oldCvFilePath);
            if (parsedUrl) {
              supabase.storage
                .from(parsedUrl.bucket)
                .remove([parsedUrl.filePath])
                .then(({ error }) => {
                  if (error) {
                    console.warn('Error deleting old CV file:', error);
                  } else {
                    console.log('Old CV file deleted successfully');
                  }
                });
            }
          }).catch(error => {
            console.warn('Error processing old CV file URL:', error);
          });
        }
      }

      setUploadStatus('success');

      // Close the modal after a short delay to show success state
      setTimeout(() => {
        setPhotoUploadOpen(false);
        setSelectedFile(null);
        setUploadStatus('idle');
      }, 1500);

    } catch (error) {
      console.error('Error uploading photo:', error);
      setUploadStatus('error');

      // Show user-friendly error message
      if (error?.message?.includes('Unauthorized') || error?.message?.includes('row-level security')) {
        console.error('Storage permission error. Please run the storage setup SQL script.');
        alert('Upload failed: Storage permissions not configured. Please contact support or check the setup_storage_buckets.sql file.');
      } else {
        alert(`Upload failed: ${error?.message || 'Unknown error occurred'}`);
      }
    }
  };

  // Initialize edit data when entering edit mode
  useEffect(() => {
    if (isEditing) {
      setEditData({ ...storeProfileData, ...pendingChanges });
    }
  }, [isEditing, storeProfileData, pendingChanges, setEditData]);

  // Handle input changes
  const handleInputChange = (field: string, value: any) => {
    updateEditData(field, value);
  };

  // Handle save
  const handleSave = () => {
    updateProfile(editData);
  };

  // Handle cancel
  const handleCancel = () => {
    setIsEditing(false);
    resetEditData();
  };

  // State for dedicated Edit Bio modal
  const [isBioModalOpen, setIsBioModalOpen] = useState(false);
  const [bioEditValue, setBioEditValue] = useState(profileData.bio || "");

  // Handler to open the Edit Bio modal
  const handleEditBio = () => {
    setBioEditValue(profileData.bio || "");
    setIsBioModalOpen(true);
  };

  // Handler to save the edited bio
  const handleSaveBio = async () => {
    await updateProfile({ ...profileData, bio: bioEditValue });
    setIsBioModalOpen(false);
  };

  // State for Teaching Experience edit modal
  const [isTeachingExpModalOpen, setIsTeachingExpModalOpen] = useState(false);
  const [teachingExpEditValue, setTeachingExpEditValue] = useState<(TutorExperience[])>(profileData.experience || []);

  // Handler to open the Teaching Experience edit modal
  const handleEditTeachingExperience = () => {
    setTeachingExpEditValue(profileData.experience ? [...profileData.experience] : []);
    setIsTeachingExpModalOpen(true);
  };

  // Handler to update a field in a specific experience entry
  const handleTeachingExpFieldChange = (idx: number, field: keyof TutorExperience, value: any) => {
    setTeachingExpEditValue(prev => prev.map((exp, i) => {
      if (i !== idx) return exp;
      let updated = { ...exp, [field]: value };
      // If start_year or end_year changes, recalculate years
      if (field === 'start_year' || field === 'end_year') {
        const start = field === 'start_year' ? value : exp.start_year;
        const end = field === 'end_year' ? value : exp.end_year;
        if (start && end) {
          const years = Number(end) - Number(start);
          updated.years = years > 0 ? years : 0;
        } else {
          updated.years = 0;
        }
      }
      return updated;
    }));
  };

  // Validation: either both start_year and end_year are filled, or both are empty
  const teachingExpValidationError = teachingExpEditValue.some(exp =>
    (exp.start_year && !exp.end_year) || (!exp.start_year && exp.end_year)
  );

  // Handler to add a new experience entry
  const handleAddTeachingExp = () => {
    setTeachingExpEditValue(prev => [
      ...prev,
      { role: '', years: 0, start_year: null, end_year: null, location: '', organization: '' }
    ]);
  };

  // Handler to remove an experience entry
  const handleRemoveTeachingExp = (idx: number) => {
    setTeachingExpEditValue(prev => prev.filter((_, i) => i !== idx));
  };

  // Handler to save the edited teaching experience
  const handleSaveTeachingExperience = async () => {
    await updateProfile({ ...profileData, experience: teachingExpEditValue });
    setIsTeachingExpModalOpen(false);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner />
        <p className="mt-4 text-gray-600">Loading your profile...</p>
      </div>
    );
  }

  // Create a simple retry function that just reloads the page
  const handleRetry = () => {
    // Simply reload the page to get a fresh state
    window.location.reload();
  };

  if (error) {
    return (
      <div className="text-center py-8 bg-red-50 rounded-lg">
        <p className="text-red-600 font-semibold">Error: {error}</p>
        <Button
          variant="outline"
          className="mt-4"
          onClick={handleRetry}
        >
          Try Again
        </Button>
      </div>
    );
  }

  // Handle save all changes
  const handleSaveAllChanges = async () => {
    try {
      await saveAllChanges();
      // Success feedback could be added here if needed
      console.log('All changes saved successfully');
    } catch (error) {
      // Error handling - the error is already logged in saveAllChanges
      console.error('Failed to save changes:', error);
      // The error state is already set in the updateProfile method
    }
  };

  // Handle discard all changes
  const handleDiscardAllChanges = () => {
    discardAllChanges();
  };

  return (
    <>
    <div className="flex flex-col md:flex-row gap-6 relative">
      {/* Left sidebar */}
      <div className="md:w-1/3 space-y-6">
        {/* Profile Photo and Basic Info */}
        <ProfileCardSection
          profileData={profileData}
          handleOpenPhotoUpload={handleOpenPhotoUpload}
          onEditBio={handleEditBio}
          onViewProfile={handleViewProfile}
        />

        {/* Profile Completeness */}
        <ProfileCompletenessSection profileData={profileData} />
      </div>

      {/* Main content area */}
      <div className="md:w-2/3 space-y-6">
        {/* General Information Section */}
        <GeneralInformationSection
          profileData={profileData}
          editData={editData}
          activeSectionEdit={activeSectionEdit}
          setActiveSectionEdit={setActiveSectionEdit}
          updateEditData={updateEditData}
          updateProfile={async (data) => addPendingChanges('general', data)}
          isModified={isSectionModified('general')}
        />

        {/* Approved Subjects Section */}
        <SubjectsSection
          profileData={profileData}
          editData={editData}
          activeSectionEdit={activeSectionEdit}
          setActiveSectionEdit={setActiveSectionEdit}
          updateEditData={updateEditData}
          updateProfile={async (data) => addPendingChanges('subjects', data)}
          isModified={isSectionModified('subjects')}
        />

        {/* Teaching Experience Section */}
        <TeachingExperienceSection profileData={profileData} onEditExperience={handleEditTeachingExperience} />

        {/* Education Section */}
        <EducationSection
          profileData={profileData}
          editData={editData}
          activeSectionEdit={activeSectionEdit}
          setActiveSectionEdit={setActiveSectionEdit}
          updateEditData={updateEditData}
          updateProfile={async (data) => addPendingChanges('education', data)}
          isModified={isSectionModified('education')}
        />

        {/* Achievements Section */}
        <AchievementsSection
          profileData={profileData}
          editData={editData}
          activeSectionEdit={activeSectionEdit}
          setActiveSectionEdit={setActiveSectionEdit}
          updateEditData={updateEditData}
          updateProfile={async (data) => addPendingChanges('achievements', data)}
          isModified={isSectionModified('achievements')}
        />

        {/* Expertise Section */}
        <ExpertiseSection
          profileData={profileData}
          editData={editData}
          activeSectionEdit={activeSectionEdit}
          setActiveSectionEdit={setActiveSectionEdit}
          updateEditData={updateEditData}
          updateProfile={async (data) => addPendingChanges('expertise', data)}
          isModified={isSectionModified('expertise')}
        />
      </div>

      {/* Floating Save Changes Button */}
      {hasPendingChanges && (
        <div className="fixed bottom-6 right-6 z-50 flex flex-col gap-2">
          <div className="bg-white p-3 rounded-lg shadow-lg border border-gray-200">
            <div className="text-sm font-medium mb-2">
              {modifiedSections.size} section{modifiedSections.size !== 1 ? 's' : ''} modified
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleDiscardAllChanges}
                className="text-red-600 border-red-200 hover:bg-red-50"
              >
                Discard
              </Button>
              <Button
                size="sm"
                onClick={handleSaveAllChanges}
                disabled={isSaving}
                className="bg-green-600 hover:bg-green-700 text-white"
              >
                {isSaving ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Saving...
                  </>
                ) : (
                  'Save All Changes'
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>

    {/* Edit Profile Modal */}
    <Modal
      isOpen={isEditing}
      onClose={handleCancel}
      title="Edit Profile"
    >
      <div className="space-y-4">
        <FormInput
          label="First Name"
          id="firstName"
          value={editData.firstName || ""}
          onChange={(e) => handleInputChange("firstName", e.target.value)}
          helpText="Your first name as it will appear on your profile"
        />

        <FormInput
          label="Last Name"
          id="lastName"
          value={editData.lastName || ""}
          onChange={(e) => handleInputChange("lastName", e.target.value)}
          helpText="Your last name as it will appear on your profile"
        />

        <FormInput
          label="Location"
          id="location"
          value={editData.location || ""}
          onChange={(e) => handleInputChange("location", e.target.value)}
          placeholder="City, Country"
          helpText="Your current city and country of residence"
        />

        <div className="flex justify-end gap-2 pt-4">
          <Button variant="outline" onClick={handleCancel}>
            <X className="h-4 w-4 mr-2" />
            Cancel
          </Button>
          <Button onClick={handleSave}>
            Save
          </Button>
        </div>
      </div>
    </Modal>

    {/* Photo Upload Modal */}
    <Modal
      isOpen={photoUploadOpen}
      onClose={() => setPhotoUploadOpen(false)}
      title={uploadType === 'profile' ? 'Upload Profile Photo' : uploadType === 'cv' ? 'Upload CV' : 'Add Photo to Gallery'}
    >
      <div className="space-y-4">
        <FileUploadPhoto
          onFileSelect={handleFileSelect}
          onFileRemove={handleFileRemove}
          buttonText={uploadType === 'profile' ? 'Select Profile Photo' : uploadType === 'cv' ? 'Select CV File' : 'Select Photo'}
          status={uploadStatus}
          fileName={selectedFile?.name} // Only shows filename after cropping is complete
          enableCropAndRotate={uploadType !== 'cv'} // Disable cropping for CV files
          onCroppedFileSelect={handleCroppedFileSelect}
        />

        <div className="flex justify-end gap-2 pt-4">
          <Button variant="outline" onClick={() => setPhotoUploadOpen(false)}>
            Cancel
          </Button>
          <Button 
            onClick={handleUploadPhoto}
            disabled={!selectedFile || uploadStatus === 'uploading'}
          >
            {uploadStatus === 'uploading' ? (
              <>
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Uploading...
              </>
            ) : 'Upload'}
          </Button>
        </div>
      </div>
    </Modal>

    {/* Dedicated Edit Bio Modal */}
    <Modal
      isOpen={isBioModalOpen}
      onClose={() => setIsBioModalOpen(false)}
      title="Edit Bio"
    >
      <div className="space-y-4">
        <FormInput
          label="Bio"
          id="bio"
          type="textarea"
          value={bioEditValue}
          onChange={e => setBioEditValue(e.target.value)}
          placeholder="Tell students about yourself, your teaching style, and experience..."
          helpText="A brief description about yourself and your teaching approach"
          rows={4}
        />

        <div className="flex justify-end gap-2 pt-4">
          <Button variant="outline" onClick={() => setIsBioModalOpen(false)}>
            Cancel
          </Button>
          <Button 
            onClick={handleSaveBio}
            disabled={bioEditValue.trim() === profileData.bio?.trim()}
          >
            Save
          </Button>
        </div>
      </div>
    </Modal>

    {/* Teaching Experience Edit Modal */}
    <Modal
      isOpen={isTeachingExpModalOpen}
      onClose={() => setIsTeachingExpModalOpen(false)}
      title="Edit Teaching Experience"
    >
      <div className="space-y-4">
        {teachingExpValidationError && (
          <div className="text-red-600 text-sm font-medium">For each entry, either both Start Year and End Year must be filled, or both must be empty.</div>
        )}
        {teachingExpEditValue.length === 0 && (
          <div className="text-center text-gray-500">No teaching experience added yet.</div>
        )}
        {teachingExpEditValue.map((exp, idx) => (
          <div key={idx} className="border rounded-lg p-4 space-y-2 relative bg-gray-50">
            <button type="button" className="absolute top-2 right-2 text-red-500 hover:text-red-700" onClick={() => handleRemoveTeachingExp(idx)} title="Remove">
              &times;
            </button>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <FormInput
                label="Role"
                id={`exp-role-${idx}`}
                value={exp.role}
                onChange={e => handleTeachingExpFieldChange(idx, 'role', e.target.value)}
                placeholder="e.g. Online, Private, Assessment"
              />
              <FormInput
                label="Organization"
                id={`exp-org-${idx}`}
                value={exp.organization}
                onChange={e => handleTeachingExpFieldChange(idx, 'organization', e.target.value)}
                placeholder="e.g. School, Company"
              />
              <FormInput
                label="Location"
                id={`exp-loc-${idx}`}
                value={exp.location}
                onChange={e => handleTeachingExpFieldChange(idx, 'location', e.target.value)}
                placeholder="e.g. City, Country"
              />
              <FormInput
                label="Years"
                id={`exp-years-${idx}`}
                type="number"
                value={exp.years !== null && exp.years !== undefined ? String(exp.years) : ""}
                disabled
                placeholder="Auto-calculated"
              />
              <FormInput
                label="Start Year"
                id={`exp-start-${idx}`}
                type="number"
                value={exp.start_year !== null && exp.start_year !== undefined ? String(exp.start_year) : ""}
                onChange={e => handleTeachingExpFieldChange(idx, 'start_year', e.target.value ? Number(e.target.value) : null)}
                placeholder="e.g. 2020"
              />
              <FormInput
                label="End Year"
                id={`exp-end-${idx}`}
                type="number"
                value={exp.end_year !== null && exp.end_year !== undefined ? String(exp.end_year) : ""}
                onChange={e => handleTeachingExpFieldChange(idx, 'end_year', e.target.value ? Number(e.target.value) : null)}
                placeholder="e.g. 2022"
              />
            </div>
          </div>
        ))}
        <Button type="button" variant="outline" onClick={handleAddTeachingExp} className="w-full">
          Add Experience
        </Button>

        <div className="flex justify-end gap-2 pt-4">
          <Button variant="outline" onClick={() => setIsTeachingExpModalOpen(false)}>
            Cancel
          </Button>
          <Button 
            onClick={handleSaveTeachingExperience}
            disabled={teachingExpValidationError || JSON.stringify(teachingExpEditValue) === JSON.stringify(profileData.experience || [])}
          >
            Save
          </Button>
        </div>
      </div>
    </Modal>

    {/* Profile View Modal */}
    {showProfileModal && (
      <TutorProfileModal
        tutor={authProfileData || profileData}
        isOpen={showProfileModal}
        onClose={handleCloseProfileModal}
        showBookingButton={false}
      />
    )}
    </>
  );
};

export default TutorProfile;